<template>
    <Modal
        :is-open="showRedemptionCreateModal"
        title="Add redemption"
        @close="tripsStore.closeModal()"
    >
        <template #subtitle>
            <div class="text-slate-500 dark:text-slate-400 text-sm leading-5 font-normal">
                {{ trip?.title }}, {{ formatDateRange(trip?.start_date, trip?.end_date) }}
                <template v-if="selectedHotel && redemptionStep > 2 && redemptionType?.slug === 'hotel'">
                    ⋅ {{ selectedHotel }}, {{ formatDateRange(hotelStartDate, hotelEndDate) }}
                </template>
                <template v-if="selectedFlight && redemptionStep > 2 && redemptionType?.slug === 'flight'">
                    ⋅ {{ selectedFlight }}, {{ formatDateRange(transportStartDate, transportEndDate) }}
                </template>
                <template v-if="selectedTrain && redemptionStep > 2 && redemptionType?.slug === 'train'">
                    ⋅ {{ selectedTrain }}, {{ formatDateRange(transportStartDate, transportEndDate) }}
                </template>
                <template v-if="selectedRentalCar && redemptionStep > 2 && redemptionType?.slug === 'rental-car'">
                    ⋅ {{ selectedRentalCar }}, {{ formatDateRange(transportStartDate, transportEndDate) }}
                </template>
                <template v-if="selectedOther && redemptionStep > 2 && redemptionType?.slug === 'other'">
                    ⋅ {{ selectedOther }}, {{ formatDateRange(transportStartDate, transportEndDate) }}
                </template>
            </div>
        </template>
        <template #content>
            <div class="mb-8">
                <RedemptionStep1 v-if="redemptionStep === 1" />
                <RedemptionStep2 v-if="redemptionStep === 2" />
                <RedemptionStep3 v-if="redemptionStep === 3" />
            </div>
            <StepIndicator />
        </template>
        <!-- Removed errors template to prevent duplicate error display -->
        <template #footer>
            <StepsActions />
        </template>
    </Modal>
</template>
<script setup>
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useTripsStore } from '@/store/trips';
import dayjs from 'dayjs';
import Modal from '@/components/ui/modal/ui-modal.vue';
import RedemptionStep1 from '@/components/trips/modals/steps/redemption-step-1.vue';
import RedemptionStep2 from '@/components/trips/modals/steps/redemption-step-2.vue';
import RedemptionStep3 from '@/components/trips/modals/steps/redemption-step-3.vue';
import StepIndicator from '@/components/trips/modals/steps/step-indicator.vue';
import StepsActions from '@/components/trips/modals/steps/steps-actions.vue';

const tripsStore = useTripsStore();

const { showRedemptionCreateModal, redemptionStep, trip, activeRedemption, redemptionType } = storeToRefs(tripsStore);

const getCityName = (airport) => {
    if (!airport) return '';
    const match = airport.match(/^([^,(-]+)/);
    return match ? match[1].trim() : airport;
};

const getTransportInfo = (type = 'flight') => {
    if (!activeRedemption.value?.travel_segments?.length) return null;
    const segment = activeRedemption.value.travel_segments[0];
    if (!segment.origin || !segment.destination) return null;

    const originName = getCityName(segment.origin_name || segment.origin);
    const destName = getCityName(segment.destination_name || segment.destination);

    return `${type.charAt(0).toUpperCase() + type.slice(1)} ${originName} – ${destName}`;
};

const selectedFlight = computed(() => {
    if (redemptionType.value?.slug !== 'flight') return null;
    return getTransportInfo('flight');
});

const selectedTrain = computed(() => {
    if (redemptionType.value?.slug !== 'train') return null;
    return getTransportInfo('train');
});

const selectedRentalCar = computed(() => {
    if (redemptionType.value?.slug !== 'rental-car') return null;
    return `Rental car ${activeRedemption.value.pickup_location || ''} – ${activeRedemption.value.dropoff_location || ''}`;
});

const selectedOther = computed(() => {
    if (redemptionType.value?.slug !== 'other') return null;
    return activeRedemption.value.name || '';
});

const transportStartDate = computed(() => {
    if (redemptionType.value?.slug === 'rental-car') {
        return activeRedemption.value?.start_date || '';
    }
    if (!activeRedemption.value?.travel_segments?.length) return '';
    return activeRedemption.value.travel_segments[0].departure_date || activeRedemption.value?.start_date || '';
});

const transportEndDate = computed(() => {
    if (redemptionType.value?.slug === 'rental-car') {
        return activeRedemption.value?.end_date || '';
    }
    if (!activeRedemption.value?.travel_segments?.length) return '';
    return activeRedemption.value.travel_segments[0].arrival_date || activeRedemption.value?.end_date || '';
});

const selectedHotel = computed(() => activeRedemption.value?.name);
const hotelStartDate = computed(() => activeRedemption.value?.start_date);
const hotelEndDate = computed(() => activeRedemption.value?.end_date);

const formatDateRange = (start, end) => {
    if (!start) return '';
    if (!end || start === end) {
        return dayjs(start).format('MMM D YYYY');
    }
    return `${dayjs(start).format('MMM D')} – ${dayjs(end).format('MMM D, YYYY')}`;
};
</script>
